/**
 * GalleryGridMode Component
 * High-density thumbnail grid for visualizing large photo collections
 *
 * STRATEGY: React-Virtuoso GroupedVirtuoso with sticky day headers
 * - Virtualized rendering for 20k+ photos
 * - Sticky day separators using GroupedVirtuoso
 * - Photos grouped by day, rendered in rows
 * - Collapsed view: shows 5 photos per day with "+X more" to expand
 */

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { GroupedVirtuoso, GroupedVirtuosoHandle } from 'react-virtuoso';
import { Empty, Spin, Tag } from 'antd';
import { Photo } from '@/types';
import { AnimatedCheckbox } from '@/components/AnimatedCheckbox';
import { MaterialIcon } from '@/components/MaterialIcon';
import { useLongPressRangeSelect } from '@/hooks/useLongPressRangeSelect';
import {
  groupPhotosForVirtuosoCollapsed,
  formatTimeInDeviceTimezone,
  type PhotoRow,
  type DayGroupWithRows,
} from '@/utils/dateUtils';

// Track loaded images globally by photoId (stable identity, not URL)
const loadedPhotoCache = new Map<string, boolean>();

// Retry configuration for thumbnails not yet generated
const RETRY_DELAYS = [2000, 3000, 5000, 8000, 12000]; // Progressive delays in ms
const SLOW_RETRY_DELAY = 15000; // After initial retries, keep retrying every 15s
const MAX_TOTAL_RETRIES = 25; // ~5 minutes total before giving up

// Custom hook to track image loading state by photoId with retry on error
const useImageLoaded = (photoId: string | undefined, thumbUrl: string | undefined) => {
  const prevPhotoIdRef = useRef(photoId);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Initialize with cached state based on photoId
  const [loaded, setLoaded] = useState(() =>
    photoId ? loadedPhotoCache.get(photoId) ?? false : false
  );
  const [retryCount, setRetryCount] = useState(0);
  const [currentUrl, setCurrentUrl] = useState(thumbUrl);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onLoad = useCallback(() => {
    if (photoId) {
      loadedPhotoCache.set(photoId, true);
    }
    setLoaded(true);
    setRetryCount(0);
  }, [photoId]);

  // Handle image load error - retry with progressive delay, then slow periodic retry
  const onError = useCallback(() => {
    if (thumbUrl && retryCount < MAX_TOTAL_RETRIES) {
      const delay = retryCount < RETRY_DELAYS.length
        ? RETRY_DELAYS[retryCount]
        : SLOW_RETRY_DELAY;
      retryTimeoutRef.current = setTimeout(() => {
        // Add cache-busting param to force reload
        const separator = thumbUrl.includes('?') ? '&' : '?';
        setCurrentUrl(`${thumbUrl}${separator}_retry=${retryCount + 1}&_t=${Date.now()}`);
        setRetryCount(prev => prev + 1);
      }, delay);
    }
  }, [retryCount, thumbUrl]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Handle cached images: if img loaded from browser cache, onLoad may not fire
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0 && !loaded) {
      onLoad();
    }
  });

  // Reset state when photoId changes
  useEffect(() => {
    if (prevPhotoIdRef.current !== photoId) {
      prevPhotoIdRef.current = photoId;
      setLoaded(photoId ? loadedPhotoCache.get(photoId) ?? false : false);
      setRetryCount(0);
      setCurrentUrl(thumbUrl);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    }
  }, [photoId, thumbUrl]);

  // Update currentUrl when thumbUrl changes (but not due to retry)
  useEffect(() => {
    if (thumbUrl && retryCount === 0) {
      setCurrentUrl(thumbUrl);
    }
  }, [thumbUrl, retryCount]);

  return { loaded, onLoad, onError, currentUrl, imgRef, isRetrying: retryCount > 0 };
};

interface GalleryGridModeProps {
  photos: Photo[];
  loading: boolean;
  selectedIds: Set<string>;
  onPhotoClick: (index: number) => void;
  onSelectionToggle: (photoId: string, index: number, shiftKey: boolean) => void;
  isSelected: (photoId: string) => boolean;
  /** Add a batch of photo IDs to the current selection (used by day "Select all") */
  onSelectMany?: (photoIds: string[]) => void;
  /** Remove a batch of photo IDs from the current selection */
  onDeselectMany?: (photoIds: string[]) => void;
  timezone?: string; // Device timezone (IANA format, e.g. "Europe/Rome")
  /** Hide selection UI (checkbox overlay, day Select-all) — used by read-only pages */
  readOnly?: boolean;
}

// Cell aspect ratio (16:9 landscape)
const ASPECT_RATIO = 16 / 9;

/**
 * Memoized photo cell component for performance
 */
interface PhotoCellProps {
  photo: Photo;
  photoIndex: number;
  cellWidth: number;
  cellHeight: number;
  isSelected: boolean;
  hasAnySelection: boolean;
  onSelectionToggle: (photoId: string, index: number, shiftKey: boolean) => void;
  onPhotoClick: (index: number) => void;
  timezone?: string;
  readOnly?: boolean;
}

const PhotoCell = React.memo<PhotoCellProps>(
  ({ photo, photoIndex, cellWidth, cellHeight, isSelected, hasAnySelection, onSelectionToggle, onPhotoClick, timezone, readOnly }) => {
    const { loaded, onLoad, onError, currentUrl, imgRef } = useImageLoaded(photo.photoId, photo.urls?.thumb200);

    return (
      <div
        data-photo-id={photo.photoId}
        style={{ width: cellWidth, height: cellHeight }}
        className="relative group cursor-pointer bg-gray-100 rounded overflow-hidden no-select-drag"
        onClick={(e) => {
          if (!loaded) return;
          const target = e.target as HTMLElement;
          // In selection mode (any photo already selected), tapping the cell
          // toggles selection instead of opening the viewer. Matches Google
          // Photos behavior.
          if (!readOnly && (target.closest('.checkbox-overlay') || hasAnySelection)) {
            onSelectionToggle(photo.photoId, photoIndex, e.shiftKey);
          } else {
            onPhotoClick(photoIndex);
          }
        }}
      >
        {/* Loading placeholder */}
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded">
            {photo.urls?.thumb200 && <Spin size="small" />}
            {!photo.urls?.thumb200 && (
              <span className="text-gray-400 text-xs">Processing...</span>
            )}
          </div>
        )}

        {/* Image - use currentUrl which handles retry with cache-busting */}
        {currentUrl && (
          <img
            ref={imgRef}
            src={currentUrl}
            alt=""
            decoding="async"
            onLoad={onLoad}
            onError={onError}
            draggable={false}
            className={`
              w-full h-full object-cover rounded no-select-drag
              ${!loaded ? 'invisible' : ''}
              ${isSelected ? 'ring-2 ring-blue-300 ring-inset' : 'hover:ring-2 hover:ring-gray-300 hover:ring-inset hover:opacity-90'}
            `}
          />
        )}

        {/* Checkbox overlay - no z-index needed, sticky header handles stacking */}
        {loaded && !readOnly && (
          <div
            className={`checkbox-overlay absolute top-1 left-1 transition-opacity duration-150 ${isSelected || hasAnySelection ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
            onClick={(e) => {
              e.stopPropagation();
              onSelectionToggle(photo.photoId, photoIndex, e.shiftKey);
            }}
          >
            <AnimatedCheckbox
              checked={isSelected}
              onChange={(e) => onSelectionToggle(photo.photoId, photoIndex, e.shiftKey)}
              size={20}
              id={photo.photoId}
            />
          </div>
        )}

        {/* RAW badge */}
        {loaded && photo.rawUrl && (
          <div className="absolute top-1 right-1">
            <Tag className="overlay-tag">
              RAW
            </Tag>
          </div>
        )}

        {/* Time tag - bottom center */}
        {loaded && photo.capturedAt && (
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
            <Tag className="overlay-tag">
              {formatTimeInDeviceTimezone(photo.capturedAt, timezone)}
            </Tag>
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.photo.photoId === nextProps.photo.photoId &&
    prevProps.photo.rawUrl === nextProps.photo.rawUrl &&
    prevProps.photo.urls?.thumb200 === nextProps.photo.urls?.thumb200 &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.hasAnySelection === nextProps.hasAnySelection &&
    prevProps.cellWidth === nextProps.cellWidth &&
    prevProps.cellHeight === nextProps.cellHeight &&
    prevProps.timezone === nextProps.timezone
);
PhotoCell.displayName = 'PhotoCell';

/**
 * "Show X more" cell for collapsed days
 */
interface MoreCellProps {
  remainingCount: number;
  cellWidth: number;
  cellHeight: number;
  onExpand: () => void;
}

const MoreCell = React.memo<MoreCellProps>(({ remainingCount, cellWidth, cellHeight, onExpand }) => (
  <div
    style={{ width: cellWidth, height: cellHeight }}
    className="relative group cursor-pointer bg-gray-200 rounded overflow-hidden flex items-center justify-center hover:bg-gray-300 transition-colors"
    onClick={onExpand}
  >
    <span className="text-gray-600 font-medium text-sm">+{remainingCount} more</span>
  </div>
));
MoreCell.displayName = 'MoreCell';

/**
 * Memoized photo row component
 */
interface PhotoRowComponentProps {
  row: PhotoRow;
  columnCount: number;
  cellWidth: number;
  cellHeight: number;
  gap: number;
  isSelected: (photoId: string) => boolean;
  hasAnySelection: boolean;
  onPhotoClick: (index: number) => void;
  onSelectionToggle: (photoId: string, index: number, shiftKey: boolean) => void;
  timezone?: string;
  /** If this is the last row and has remaining photos, show MoreCell */
  remainingCount?: number;
  onExpand?: () => void;
  /** Is this the last row of the day group? */
  isLastRowOfGroup?: boolean;
  readOnly?: boolean;
}

const PhotoRowComponent = React.memo<PhotoRowComponentProps>(
  ({ row, columnCount, cellWidth, cellHeight, gap, isSelected, hasAnySelection, onPhotoClick, onSelectionToggle, timezone, remainingCount, onExpand, isLastRowOfGroup, readOnly }) => {
    const photos = row.photos;
    const showMoreCell = remainingCount !== undefined && remainingCount > 0 && onExpand;

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
          gap: `${gap}px`,
          paddingTop: gap / 2,
          paddingBottom: isLastRowOfGroup ? gap * 2 : gap / 2,
        }}
      >
        {photos.map((photo, i) => (
          <PhotoCell
            key={photo.photoId}
            photo={photo}
            photoIndex={row.indices[i]}
            cellWidth={cellWidth}
            cellHeight={cellHeight}
            isSelected={isSelected(photo.photoId)}
            hasAnySelection={hasAnySelection}
            onPhotoClick={onPhotoClick}
            onSelectionToggle={onSelectionToggle}
            timezone={timezone}
            readOnly={readOnly}
          />
        ))}
        {showMoreCell && (
          <MoreCell
            remainingCount={remainingCount}
            cellWidth={cellWidth}
            cellHeight={cellHeight}
            onExpand={onExpand}
          />
        )}
      </div>
    );
  }
);
PhotoRowComponent.displayName = 'PhotoRowComponent';

/**
 * Day separator / group header with expand/collapse toggle
 */
interface GroupHeaderProps {
  group: DayGroupWithRows;
  isExpanded: boolean;
  onToggle: (dateKey: string) => void;
  onSelectDay?: (dateKey: string) => void;
  allDaySelected?: boolean;
}

const GroupHeader = React.memo<GroupHeaderProps>(({ group, isExpanded, onToggle, onSelectDay, allDaySelected }) => {
  // Show toggle if there are hidden photos (remainingCount > 0) or day is expanded
  const canToggle = isExpanded || (group.remainingCount !== undefined && group.remainingCount > 0);

  return (
    <div className="day-separator" data-datekey={group.dateKey}>
      <div className="day-separator__left">
        {onSelectDay && (
          <button
            className={`day-separator__select-all${allDaySelected ? ' day-separator__select-all--active' : ''}`}
            onClick={() => onSelectDay(group.dateKey)}
            title={allDaySelected ? "Unselect all photos of this day" : "Select all photos of this day"}
          >
            {allDaySelected ? 'Unselect' : 'Select'}
          </button>
        )}
        {/* Full date on larger screens, short on small screens */}
        <span className="day-separator__date day-separator__date--full">{group.displayDate}</span>
        <span className="day-separator__date day-separator__date--short">{group.displayDateShort}</span>
        <span className="day-separator__count">
          • {group.photoCount} {group.photoCount === 1 ? 'photo' : 'photos'}
        </span>
      </div>
      {canToggle && (
        <button
          className="day-separator__toggle"
          onClick={() => onToggle(group.dateKey)}
          title={isExpanded ? 'Collapse' : 'Expand'}
        >
          <MaterialIcon name={isExpanded ? 'remove' : 'add'} size={18} />
        </button>
      )}
    </div>
  );
});
GroupHeader.displayName = 'GroupHeader';

export function GalleryGridMode({
  photos,
  loading,
  selectedIds,
  onPhotoClick,
  onSelectionToggle,
  isSelected,
  onSelectMany,
  onDeselectMany,
  timezone,
  readOnly,
}: GalleryGridModeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<GroupedVirtuosoHandle>(null);
  const virtuosoScrollerRef = useRef<HTMLElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(1200);
  const [containerHeight, setContainerHeight] = useState(600);
  // Suppress the synthetic click that follows a long-press + drag release on touch.
  const suppressTapUntilRef = useRef(0);
  // Track which days are expanded (show all photos)
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  // Track pending scroll after collapse - stores dateKey and its viewport offset
  const pendingScrollRef = useRef<{ dateKey: string; viewportOffset: number } | null>(null);
  // Track currently visible (sticky) day dateKey - updated via rangeChanged callback
  const currentStickyDayRef = useRef<string | null>(null);
  // Map row index to group dateKey for quick lookup
  const rowToGroupDateKeyRef = useRef<Map<number, string>>(new Map());
  // Track last clicked photo for scroll anchoring
  const lastClickedPhotoRef = useRef<{ photoId: string; viewportOffset: number } | null>(null);

  // Measure container width for column count and viewport-based height.
  // Height = space between container top and viewport bottom (minus buffer).
  // Uses visualViewport on mobile for accuracy with dynamic URL bars.
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerWidth(rect.width);
        const viewportH = window.visualViewport?.height ?? window.innerHeight;
        const availableHeight = viewportH - rect.top - 24;
        setContainerHeight(Math.max(400, availableHeight));
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    window.visualViewport?.addEventListener('resize', updateDimensions);
    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.visualViewport?.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Grid configuration
  const MIN_CELL_WIDTH = 160;
  const GAP = 8;
  const columnCount = Math.max(2, Math.floor(containerWidth / (MIN_CELL_WIDTH + GAP)));
  const cellWithGap = containerWidth / columnCount;
  const cellWidth = Math.floor(cellWithGap - GAP);
  const cellHeight = Math.floor(cellWidth / ASPECT_RATIO);
  const rowHeight = cellHeight + GAP;
  const hasAnySelection = selectedIds.size > 0;

  // Wrap onPhotoClick so that taps immediately following a long-press+drag
  // release are ignored (iOS/Android fire a synthetic click).
  const handlePhotoClick = useCallback(
    (index: number) => {
      if (Date.now() < suppressTapUntilRef.current) return;
      onPhotoClick(index);
    },
    [onPhotoClick]
  );

  // Wrapper for selection toggle that captures element position for scroll anchoring
  const handleSelectionToggle = useCallback(
    (photoId: string, index: number, shiftKey: boolean) => {
      // Find the photo cell element by data attribute to get its position
      const photoCell = containerRef.current?.querySelector(`[data-photo-id="${photoId}"]`) as HTMLElement | null;

      if (photoCell) {
        const rect = photoCell.getBoundingClientRect();
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (containerRect) {
          // Store the offset from the top of the virtuoso container
          lastClickedPhotoRef.current = {
            photoId,
            viewportOffset: rect.top - containerRect.top,
          };
        }
      }
      onSelectionToggle(photoId, index, shiftKey);
    },
    [onSelectionToggle]
  );

  // Handler for toggling expand/collapse of a day
  const handleToggleExpand = useCallback((dateKey: string) => {
    // Capture current position of the day header before state change
    const dayHeader = containerRef.current?.querySelector(`[data-datekey="${dateKey}"]`) as HTMLElement | null;
    let viewportOffset = 0;
    if (dayHeader) {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (containerRect) {
        viewportOffset = dayHeader.getBoundingClientRect().top - containerRect.top;
      }
    }

    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        // Collapsing - schedule scroll to keep day in same position
        next.delete(dateKey);
        pendingScrollRef.current = { dateKey, viewportOffset };
      } else {
        next.add(dateKey);
      }
      return next;
    });
  }, []);

  // Compute grouped data with collapsed view (max 5 photos per day, unless expanded)
  const MAX_PHOTOS_PER_DAY = 5;
  const photoGroups = useMemo(
    () => groupPhotosForVirtuosoCollapsed(photos, columnCount, MAX_PHOTOS_PER_DAY, expandedDays),
    [photos, columnCount, expandedDays]
  );

  // Scroll to day after collapse to keep it in the same viewport position
  useEffect(() => {
    if (pendingScrollRef.current) {
      const { dateKey, viewportOffset } = pendingScrollRef.current;
      pendingScrollRef.current = null;

      // Find group index by dateKey
      const groupIndex = photoGroups.groups.findIndex(g => g.dateKey === dateKey);
      if (groupIndex === -1) return;

      // Calculate the scroll position for this group's header
      const HEADER_HEIGHT = 44; // 36px header + 8px border
      let headerPosition = 0;
      for (let i = 0; i < groupIndex; i++) {
        headerPosition += HEADER_HEIGHT + (photoGroups.groupCounts[i] * rowHeight);
      }

      // Calculate target scroll to keep header at same viewport offset
      const targetScrollTop = headerPosition - viewportOffset;

      requestAnimationFrame(() => {
        const virtuosoElement = containerRef.current?.querySelector('[data-virtuoso-scroller="true"]') as HTMLElement
          || containerRef.current?.firstElementChild as HTMLElement;

        if (virtuosoElement) {
          virtuosoElement.scrollTop = Math.max(0, targetScrollTop);
        }
      });
    }
  }, [photoGroups, rowHeight]);

  // Scroll anchor: after expand/collapse, keep the clicked photo in the same viewport position
  useEffect(() => {
    if (!lastClickedPhotoRef.current || !virtuosoRef.current) return;

    const { photoId, viewportOffset } = lastClickedPhotoRef.current;

    // Find the row index containing this photo
    let targetRowIndex = -1;
    for (let i = 0; i < photoGroups.allRows.length; i++) {
      const row = photoGroups.allRows[i];
      if (row.photos.some(p => p.photoId === photoId)) {
        targetRowIndex = i;
        break;
      }
    }

    if (targetRowIndex === -1) {
      lastClickedPhotoRef.current = null;
      return;
    }

    // Calculate target scroll position:
    // We need to find where the row will be after expand and adjust scroll
    // to keep it at the same viewport offset
    // Count rows and headers before the target row
    let rowsBeforeTarget = 0;
    let headersBeforeTarget = 0;
    let currentRowCount = 0;
    for (let groupIdx = 0; groupIdx < photoGroups.groups.length; groupIdx++) {
      const groupRowCount = photoGroups.groupCounts[groupIdx];
      if (currentRowCount + groupRowCount > targetRowIndex) {
        // Target is in this group
        rowsBeforeTarget = targetRowIndex;
        headersBeforeTarget = groupIdx;
        break;
      }
      currentRowCount += groupRowCount;
      headersBeforeTarget = groupIdx + 1;
    }

    // Estimate scroll position (header height ~36px based on CSS)
    const HEADER_HEIGHT = 36;
    const estimatedScrollTop = (rowsBeforeTarget * rowHeight) + (headersBeforeTarget * HEADER_HEIGHT) - viewportOffset;

    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      // Find the Virtuoso scroller element (first child with overflow-y)
      const virtuosoElement = containerRef.current?.querySelector('[data-virtuoso-scroller="true"]') as HTMLElement
        || containerRef.current?.firstElementChild as HTMLElement;

      if (virtuosoElement) {
        virtuosoElement.scrollTop = Math.max(0, estimatedScrollTop);
      }
      lastClickedPhotoRef.current = null;
    });
  }, [photoGroups, rowHeight]);

  // Build a map of row index -> dateKey (for sticky day tracking)
  // Also track which row is the last row of each group (for extra padding) and collapsed groups (for MoreCell)
  const rowMetaMap = useMemo(() => {
    const dateKeyMap = new Map<number, string>();
    const rowInfo = new Map<number, { dateKey: string; remainingCount?: number; isLastRow: boolean }>();
    let rowIndex = 0;
    for (const group of photoGroups.groups) {
      for (let i = 0; i < group.rows.length; i++) {
        const isLastRow = i === group.rows.length - 1;
        dateKeyMap.set(rowIndex, group.dateKey);
        rowInfo.set(rowIndex, {
          dateKey: group.dateKey,
          remainingCount: isLastRow && group.remainingCount && group.remainingCount > 0 ? group.remainingCount : undefined,
          isLastRow,
        });
        rowIndex++;
      }
    }
    // Update ref for rangeChanged callback
    rowToGroupDateKeyRef.current = dateKeyMap;
    return rowInfo;
  }, [photoGroups.groups]);

  // Handle visible range changes to track the sticky day
  const handleRangeChanged = useCallback((range: { startIndex: number; endIndex: number }) => {
    const dateKey = rowToGroupDateKeyRef.current.get(range.startIndex);
    if (dateKey) {
      currentStickyDayRef.current = dateKey;
    }
  }, []);

  // Handler to select all photos of a given day, or deselect them if already all selected
  const handleSelectDay = useCallback(
    (dateKey: string) => {
      if (!onSelectMany) return;
      const group = photoGroups.groups.find((g) => g.dateKey === dateKey);
      if (!group || group.firstPhotoIndex === undefined) return;
      const start = group.firstPhotoIndex;
      const end = start + group.photoCount;
      const dayPhotoIds: string[] = [];
      for (let i = start; i < end && i < photos.length; i++) {
        dayPhotoIds.push(photos[i].photoId);
      }
      // If every photo of the day is already selected, deselect them all
      const allSelected = dayPhotoIds.length > 0 && dayPhotoIds.every((id) => selectedIds.has(id));
      if (allSelected && onDeselectMany) {
        onDeselectMany(dayPhotoIds);
      } else {
        onSelectMany(dayPhotoIds);
      }
    },
    [onSelectMany, onDeselectMany, photoGroups.groups, photos, selectedIds]
  );

  // Render group header (sticky day separator)
  const groupContent = useCallback(
    (index: number) => {
      const group = photoGroups.groups[index];
      if (!group) return null;
      let allDaySelected = false;
      if (onSelectMany && group.firstPhotoIndex !== undefined) {
        const start = group.firstPhotoIndex;
        const end = start + group.photoCount;
        allDaySelected = group.photoCount > 0;
        for (let i = start; i < end && i < photos.length; i++) {
          if (!selectedIds.has(photos[i].photoId)) {
            allDaySelected = false;
            break;
          }
        }
      }
      return (
        <GroupHeader
          group={group}
          isExpanded={expandedDays.has(group.dateKey)}
          onToggle={handleToggleExpand}
          onSelectDay={onSelectMany ? handleSelectDay : undefined}
          allDaySelected={allDaySelected}
        />
      );
    },
    [photoGroups.groups, expandedDays, handleToggleExpand, onSelectMany, handleSelectDay, selectedIds, photos]
  );

  // Render row item
  const itemContent = useCallback(
    (index: number) => {
      const row = photoGroups.allRows[index];
      if (!row) return null;

      // Get row metadata (last row of group, remaining count for collapsed)
      const rowMeta = rowMetaMap.get(index);

      return (
        <PhotoRowComponent
          row={row}
          columnCount={columnCount}
          cellWidth={cellWidth}
          cellHeight={cellHeight}
          gap={GAP}
          isSelected={isSelected}
          hasAnySelection={hasAnySelection}
          onPhotoClick={handlePhotoClick}
          onSelectionToggle={handleSelectionToggle}
          timezone={timezone}
          remainingCount={rowMeta?.remainingCount}
          onExpand={rowMeta?.remainingCount ? () => handleToggleExpand(rowMeta.dateKey) : undefined}
          isLastRowOfGroup={rowMeta?.isLastRow}
          readOnly={readOnly}
        />
      );
    },
    [photoGroups.allRows, columnCount, cellWidth, cellHeight, isSelected, hasAnySelection, handlePhotoClick, handleSelectionToggle, timezone, rowMetaMap, handleToggleExpand, readOnly]
  );

  // Long-press + drag range selection (Google Photos style) — touch only, skip read-only pages
  const photoIdToIndexRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const m = new Map<string, number>();
    for (let i = 0; i < photos.length; i++) m.set(photos[i].photoId, i);
    photoIdToIndexRef.current = m;
  }, [photos]);

  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  useLongPressRangeSelect({
    containerRef,
    disabled: readOnly || !onSelectMany || !onDeselectMany,
    photoCount: photos.length,
    indexOfPhotoId: (id) => photoIdToIndexRef.current.get(id) ?? -1,
    photoIdAt: (i) => photos[i]?.photoId,
    getCurrentSelection: () => selectedIdsRef.current,
    onSelectMany: (ids) => onSelectMany?.(ids),
    onDeselectMany: (ids) => onDeselectMany?.(ids),
    getScroller: () =>
      virtuosoScrollerRef.current
      || (containerRef.current?.querySelector('[data-virtuoso-scroller="true"]') as HTMLElement)
      || (containerRef.current?.firstElementChild as HTMLElement | null),
    onDragModeChange: (active) => {
      if (!active) suppressTapUntilRef.current = Date.now() + 350;
    },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spin size="large" />
      </div>
    );
  }

  if (photos.length === 0) {
    return <Empty description="No photos yet" className="py-20" />;
  }

  return (
    <div ref={containerRef} className="w-full">
      <GroupedVirtuoso
        ref={virtuosoRef}
        scrollerRef={(el) => {
          virtuosoScrollerRef.current = el as HTMLElement | null;
        }}
        style={{ height: containerHeight }}
        groupCounts={photoGroups.groupCounts}
        groupContent={groupContent}
        itemContent={itemContent}
        defaultItemHeight={rowHeight}
        overscan={5}
        rangeChanged={handleRangeChanged}
        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
        components={{
          Footer: () => <div style={{ height: 50 }} />,
        }}
      />
    </div>
  );
}
